import { useCallback, useState } from 'react';

import { ApiError, createEndpoint, extendEndpoint, type CreatedEndpoint } from '../lib/api.js';
import { expiresSoon, hasExpired, timeUntil } from '../lib/format.js';
import { inspectorPath, navigate } from '../lib/router.js';
import {
  forgetEndpoint,
  listSaved,
  refreshSaved,
  saveEndpoint,
  type SavedEndpoint,
} from '../lib/storage.js';
import { CopyButton } from '../components/CopyButton.js';

export function Home() {
  const [saved, setSaved] = useState<SavedEndpoint[]>(() => listSaved());
  const [created, setCreated] = useState<CreatedEndpoint>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [renewing, setRenewing] = useState<ReadonlySet<string>>(new Set());

  const onCreate = useCallback(async () => {
    setBusy(true);
    setError(undefined);

    try {
      const endpoint = await createEndpoint();
      saveEndpoint(endpoint);
      setCreated(endpoint);
      setSaved(listSaved());
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === 'rate_limited'
          ? 'Has creado demasiados endpoints desde esta IP. Prueba dentro de un rato.'
          : 'No se pudo crear el endpoint. Inténtalo de nuevo.',
      );
    } finally {
      setBusy(false);
    }
  }, []);

  const onForget = useCallback((slug: string) => {
    forgetEndpoint(slug);
    setSaved(listSaved());
  }, []);

  const onRenew = useCallback(async (endpoint: SavedEndpoint) => {
    setRenewing((current) => new Set(current).add(endpoint.slug));
    setError(undefined);

    try {
      const updated = await extendEndpoint(endpoint.slug, endpoint.token);
      refreshSaved(updated);
      setSaved(listSaved());
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.isGone
          ? 'El endpoint ya no existe: la limpieza lo borró.'
          : 'No se pudo renovar la caducidad.',
      );
    } finally {
      setRenewing((current) => {
        const next = new Set(current);
        next.delete(endpoint.slug);
        return next;
      });
    }
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-6 py-12 sm:py-20">
      <h1 className="text-4xl font-semibold tracking-tight text-slate-100">Mock API</h1>
      <p className="mt-3 max-w-xl text-slate-400">
        Endpoints temporales para depurar webhooks e integraciones. Sin cuenta, sin email: pulsa el
        botón y ya tienes una URL pública que guarda todo lo que le llegue.
      </p>

      <button
        type="button"
        onClick={() => void onCreate()}
        disabled={busy}
        className="mt-8 rounded-lg bg-sky-500 px-5 py-3 font-semibold text-slate-950 transition hover:bg-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:opacity-60"
      >
        {busy ? 'Creando…' : 'Crear un endpoint'}
      </button>

      {error && (
        <p role="alert" className="mt-4 text-sm text-rose-300">
          {error}
        </p>
      )}

      {created && <CreatedCard endpoint={created} />}

      {saved.length > 0 && (
        <section className="mt-12">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Tus endpoints en este navegador
          </h2>

          <ul className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
            {saved.map((endpoint) => (
              <SavedRow
                key={endpoint.slug}
                endpoint={endpoint}
                renewing={renewing.has(endpoint.slug)}
                onForget={onForget}
                onRenew={onRenew}
              />
            ))}
          </ul>

          <p className="mt-3 text-xs text-slate-500">
            Se guardan solo en este navegador. Si borras los datos del sitio pierdes los tokens y
            con ellos el acceso a los endpoints.
          </p>
        </section>
      )}
    </main>
  );
}

function CreatedCard({ endpoint }: { endpoint: CreatedEndpoint }) {
  return (
    <section className="mt-8 rounded-xl border border-sky-500/30 bg-sky-500/5 p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-sky-300">
        Tu endpoint está listo
      </h2>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded-md bg-slate-950/70 px-3 py-2 font-mono text-sm text-slate-100">
          {endpoint.url}
        </code>
        <CopyButton
          value={endpoint.url}
          label="Copiar URL"
          className="rounded-md bg-sky-500 px-3 py-2 text-xs font-semibold text-slate-950 transition hover:bg-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        />
      </div>

      <p className="mt-3 text-sm text-slate-400">
        Caduca {timeUntil(endpoint.expiresAt)}. El token de gestión ya está guardado en este
        navegador; la API solo lo devuelve una vez.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate(inspectorPath(endpoint.slug))}
          className="rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        >
          Abrir el inspector
        </button>
        <CopyButton value={endpoint.token} label="Copiar token" />
      </div>
    </section>
  );
}

function SavedRow({
  endpoint,
  renewing,
  onForget,
  onRenew,
}: {
  endpoint: SavedEndpoint;
  renewing: boolean;
  onForget: (slug: string) => void;
  onRenew: (endpoint: SavedEndpoint) => void;
}) {
  const expired = hasExpired(endpoint.expiresAt);
  const soon = expiresSoon(endpoint.expiresAt);

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <button
        type="button"
        onClick={() => navigate(inspectorPath(endpoint.slug))}
        className="min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
      >
        <span className="block truncate font-mono text-sm text-slate-200">/e/{endpoint.slug}</span>
        <span
          className={`text-xs ${expired ? 'text-rose-400' : soon ? 'text-amber-400' : 'text-slate-500'}`}
        >
          {expired ? 'Caducado' : `Caduca ${timeUntil(endpoint.expiresAt)}`}
        </span>
      </button>

      {(soon || expired) && (
        <button
          type="button"
          onClick={() => onRenew(endpoint)}
          disabled={renewing}
          className="rounded-md border border-sky-500/50 bg-sky-500/10 px-2.5 py-1 text-xs font-semibold text-sky-300 transition hover:bg-sky-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-50"
        >
          {renewing ? 'Renovando…' : 'Renovar'}
        </button>
      )}

      <button
        type="button"
        onClick={() => onForget(endpoint.slug)}
        className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-400 transition hover:border-rose-500/50 hover:text-rose-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
      >
        Olvidar
      </button>
    </li>
  );
}
